import { useAuth, useUser } from "@clerk/nextjs";
import { useEffect, useRef } from "react";
import { useFoodangoUser } from "@/features/analytics/hooks/use-foodango-user";
import {
  getFuidCookie,
  removeFuidCookie,
  setFuidCookie,
  setFuidCookieNull,
} from "@/features/analytics/lib/fuid-cookie";

export const useFuidCookie = () => {
  const { userId } = useAuth();
  const { user, isLoaded } = useUser();
  const { data: foodangoUserQuery, isPending } = useFoodangoUser({
    email: user?.primaryEmailAddress?.emailAddress,
  });

  const previousUserIdRef = useRef<string | null | undefined>(undefined);
  const justLoggedOutRef = useRef(false);

  // Monitor user logout and set fuid cookie to null
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;

    // Detect logout: had userId before, now it's null
    if (previousUserId && userId === null) {
      setFuidCookieNull();
      justLoggedOutRef.current = true;
    }

    // Update ref with current userId
    previousUserIdRef.current = userId;
  }, [userId]);

  // Manage fuid cookie based on user login state
  useEffect(() => {
    if (!isLoaded) return;

    const currentFuidCookie = getFuidCookie();

    // Check if cookie is "null" from previous logout and remove it
    // Only remove if this is not a fresh logout (should wait for page refresh)
    if (currentFuidCookie === "null") {
      if (justLoggedOutRef.current) {
        // Reset the flag - cookie will be removed on next page load
        justLoggedOutRef.current = false;
      } else {
        removeFuidCookie();
      }
      return;
    }

    // User is not logged in - nothing to do
    if (!user) {
      return;
    }

    // User is logged in - wait for Foodango data
    const hasEmail = user.primaryEmailAddress?.emailAddress;
    if (hasEmail && isPending) return;

    // Set or update fuid cookie with Foodango user ID
    const foodangoUserId = foodangoUserQuery?.id;
    if (!foodangoUserId) return;

    // Update cookie if value changed
    if (currentFuidCookie !== foodangoUserId) {
      setFuidCookie(foodangoUserId);
    }
  }, [isLoaded, user, foodangoUserQuery, isPending]);
};
